// src/services/bankDocumentService.ts
import apiService from './api';

export interface BankDocument {
  id: number;
  name: string;
  original_name: string;
  file_url: string;
  file_size: number;
  file_type: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

interface CloudFile {
  id: number;
  file_url: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  created_at: string;
  folder_id?: number;
  name?: string;
}

interface Folder {
  id: number;
  name: string;
  parent_id?: number;
  created_at: string;
  path: string;
  user_id: string;
}

interface StorageInfo {
  employeeId: number;
  allocationGB: number;
  usedSpaceGB: string;
  freeSpaceGB: string | null;
  files: CloudFile[];
}

interface FolderData {
  folders: Folder[];
}

interface FolderPathData {
  path: Array<{id: number; name: string}>;
}

interface CreateFolderRequest {
  name: string;
  parent_id: number | null;
  user_id: string;
}

interface SearchResult {
    query: string;
    documents: CloudFile[];
    folders: Folder[];
}

export const bankDocumentService = {
  // Get all bank documents
  getDocuments: async (): Promise<BankDocument[]> => {
    const response = await apiService.get<BankDocument[]>('/bank-documents');
    return response.data;
  },

  getDocumentsCloud: async (id: string, folderId: number | null = null): Promise<StorageInfo> => {
    const url = folderId 
      ? `/documents/cloud-files/${id}?folder_id=${folderId}` 
      : `/documents/cloud-files/${id}`;
    const response = await apiService.get<StorageInfo>(url);
    return response.data;
  },

  getDocumentsCloudAdmin: async (id: string, folderId: number | null = null): Promise<StorageInfo> => {
    const url = folderId 
      ? `/documents/get-cloud-files/${id}?folder_id=${folderId}` 
      : `/documents/get-cloud-files/${id}`;
    const response = await apiService.get<StorageInfo>(url);
    return response.data;
  },

  uploadDocumentCloud: async (formData: FormData, folderId?: number | null): Promise<any> => {
    // No need to modify the URL as folder_id is already in the formData
    const response = await apiService.post('/documents/cloud-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload a new bank document
  uploadDocument: async (formData: FormData): Promise<any> => {
    const response = await apiService.post('/bank-documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete a bank document
  deleteDocument: async (id: number): Promise<void> => {
    await apiService.delete(`/bank-documents/${id}`);
  },

  deleteDocumentCloud: async (id: number): Promise<void> => {
    await apiService.delete(`/documents/cloud-delete/${id}`);
  },

  // Get bank document by ID (if you implement this endpoint)
  getDocument: async (id: number): Promise<BankDocument> => {
    const response = await apiService.get<BankDocument>(`/bank-documents/${id}`);
    return response.data;
  },

  // Update bank document name
  updateDocument: async (id: number, name: string): Promise<BankDocument> => {
    const response = await apiService.put<BankDocument>(`/bank-documents/${id}`, { name });
    return response.data;
  },

  // Folder management methods
  getFolders: async (userId: string): Promise<FolderData> => {
    const response = await apiService.get<FolderData>(`/documents/folders/${userId}`);
    return response.data;
  },

  createFolder: async (folderData: CreateFolderRequest): Promise<Folder> => {
    const response = await apiService.post<Folder>('/documents/create-folder', folderData);
    return response.data;
  },

  deleteFolder: async (folderId: number): Promise<void> => {
    await apiService.delete(`/documents/delete-folder/${folderId}`);
  },

  getFolderPath: async (folderId: number): Promise<FolderPathData> => {
    const response = await apiService.get<FolderPathData>(`/documents/folder-path/${folderId}`);
    return response.data;
  },

  moveDocument: async (documentId: number, folderId: number | null): Promise<void> => {
    await apiService.put(`/documents/move-document/${documentId}`, { folder_id: folderId });
  },

  moveFolder: async (folderId: number, parentFolderId: number | null): Promise<void> => {
    await apiService.put<void>(`/documents/move-folder/${folderId}`, { parent_id: parentFolderId });
  },

  renameFolder: async (folderId: number, name: string): Promise<Folder> => {
    const response = await apiService.put<Folder>(`/documents/rename-folder/${folderId}`, { name });
    return response.data;
  },

  searchDocuments: async (userId: string, query: string): Promise<SearchResult> => {
    const response = await apiService.get<SearchResult>(
      `/documents/search/${userId}`,
      { params: { q: query } }
    );
    return response.data;
  }
};